import React from 'react';

import { Box, Button, Grid, Skeleton, Typography } from '@mui/material';
import { last } from 'es-toolkit';

import { Field, Form } from '@hrlogics/react-components/form';

import { appRoutes, SSN_MASK } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import { EmploymentDataInfoFragment, SignUpMethod } from 'src/api';
import { Link } from 'src/components/common';
import {
  AddressFields,
  MaskedTextField,
  PasswordField,
  PhoneField,
  TextField,
} from 'src/components/finalForm';
import { EmployeeBasicInfoFormField } from 'src/constants/signup';
import { useAuthSignupConsumer } from 'src/hooks/auth';
import { SxProp } from 'src/types/mui';
import { EmployeeBasicInfoFormValues } from 'src/types/signup';

import { employeeBasicInfoFormValidationSchema } from '../utils';

export type ConsumerBasicInfoFormProps = {
  onSubmit: (formValues: EmployeeBasicInfoFormValues) => void;
  signUpMethod: Nullable<string>;
  displayName: Nullable<string>;
  loading?: boolean;
};

export const ConsumerBasicInfoForm: React.FC<ConsumerBasicInfoFormProps> = ({
  onSubmit,
  loading,
  signUpMethod,
  displayName,
}) => {
  const { formData } = useAuthSignupConsumer();

  const positions: EmploymentDataInfoFragment[] = (formData.positions ||
    []) as EmploymentDataInfoFragment[];
  const lastPosition: EmploymentDataInfoFragment | undefined = last(positions);

  const initialValues: Partial<EmployeeBasicInfoFormValues> = React.useMemo(
    () => ({
      ssn: (formData.ssn as string) || '',
      firstName: lastPosition?.employeeInformation.firstName || '',
      lastName: lastPosition?.employeeInformation.lastName || '',
      email: formData.email ? String(formData.email) : '',
    }),
    [
      formData.email,
      formData.ssn,
      lastPosition?.employeeInformation.firstName,
      lastPosition?.employeeInformation.lastName,
    ],
  );

  const isEmailDisabled =
    initialValues.email && (signUpMethod === SignUpMethod.Ssn || signUpMethod === SignUpMethod.Pin);

  return (
    <Box>
      <Box display="flex" alignItems="center">
        <Typography variant="body2" fontSize="24px">
          Sign up for the company&nbsp;
        </Typography>
        {loading ? (
          <Skeleton width={100} height={40} />
        ) : (
          <Typography variant="body2" fontSize="24px">
            {displayName}
          </Typography>
        )}
      </Box>
      <Typography variant="body2" fontSize="16px" mt={1}>
        Already have an account? <Link to={appRoutes.auth.login}>Log in</Link>
      </Typography>
      <Form<EmployeeBasicInfoFormValues>
        initialValues={initialValues}
        onSubmit={onSubmit}
        subscription={{ valid: true, submitting: true }}
        containerProps={{ mt: 4 }}
        validationSchema={employeeBasicInfoFormValidationSchema}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <Grid container spacing={1.5}>
                <Grid size={12}>
                  <Field
                    name={EmployeeBasicInfoFormField.ssn}
                    component={MaskedTextField}
                    label="SSN"
                    mask={SSN_MASK}
                    disabled={signUpMethod === SignUpMethod.Ssn}
                    fullWidth
                    required
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    component={TextField}
                    name={EmployeeBasicInfoFormField.firstName}
                    label="First name"
                    fullWidth
                    disabled={submitting}
                    required
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    component={TextField}
                    name={EmployeeBasicInfoFormField.lastName}
                    label="Last name"
                    fullWidth
                    disabled={submitting}
                    required
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    component={TextField}
                    name={EmployeeBasicInfoFormField.email}
                    label="Email"
                    fullWidth
                    disabled={isEmailDisabled || submitting}
                    required
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    name={EmployeeBasicInfoFormField.phoneNumber}
                    label="Phone number"
                    required
                    fullWidth
                    disabled={submitting}
                    component={PhoneField}
                  />
                </Grid>

                <Grid size={12}>
                  <AddressFields
                    prefix={EmployeeBasicInfoFormField.homeAddress}
                    required
                    disabled={submitting}
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    name={EmployeeBasicInfoFormField.password}
                    fullWidth
                    label="Password"
                    required
                    component={PasswordField}
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    name={EmployeeBasicInfoFormField.confirmPassword}
                    fullWidth
                    label="Confirm password"
                    required
                    component={PasswordField}
                  />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="center" mt={4}>
                <Button
                  type="submit"
                  sx={submitButtonStyles}
                  disabled={submitting || !valid}
                  loading={submitting}
                  size="large"
                  variant="contained"
                >
                  Next
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </Box>
  );
};

const submitButtonStyles: SxProp = {
  px: 5,
};
